import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function GoogleARMentalHealthPage() {
  useSEO({
    title: 'Google AR Mental Health Innovation | Design For',
    description: 'Exploring augmented reality for mental health support. Innovation consulting for digital mental health interventions.',
    keywords: 'augmented reality, mental health, digital health, healthcare innovation, AR technology',
    canonical: '/work/google-ar-mental-health',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Exploring augmented reality for mental health support',
    description: 'We partnered with Google to explore how augmented reality technology can support mental health interventions, creating innovative digital health experiences.',
    url: 'https://example.com/work/google-ar-mental-health',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Technology company - AR mental health innovation',
    keywords: 'augmented reality, mental health, digital health, healthcare innovation, AR technology',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Revitalising mental health in Hong Kong through AR &amp; AI
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Google</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                Following the 2020 lockdowns, Google wanted to create an initiative that would help reconnect communities, revitalise the economy, and support mental wellbeing in a culturally meaningful way. The challenge was designing an experience that resonated across generations while delivering measurable social impact.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                We helped bring six AR-enabled characters to life, each designed to encourage people to get outside, reconnect, and do something positive together. Working closely with governments and partners across Asia, the initiative blended emerging technology with human-centred storytelling to address mental health in a new, engaging way.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Built partnerships across Google, HSBC, and Save the Children',
                  'Delivered an AR-enabled mental health experience with six distinct characters',
                  'Created a printable wellbeing activity pack to support families',
                  'Achieved 3,000+ app downloads',
                  'Connected the experience to a health outcomes platform to track impact',
                  'Worked with governments and partners across Asia to scale the initiative',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-[#f8f8f8] rounded-2xl flex items-center justify-center p-6 min-h-[300px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/b0dfaab9dbf250d7d35ccf78d2a92b0d.png"
                  alt="Google AR Mental Health Hong Kong characters"
                  className="max-w-full max-h-[380px] object-contain"
                />
              </div>
              <div className="bg-[#f8f8f8] rounded-2xl flex items-center justify-center p-6 min-h-[300px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/983b2cf2226e0ba05a8262de5bbfcb7c.jpeg"
                  alt="AR interface for mental health experience"
                  className="max-w-full max-h-[380px] object-contain"
                />
              </div>
              <div className="bg-[#f8f8f8] rounded-2xl flex items-center justify-center p-6 min-h-[260px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/fce79fadc8843bb51ba57327b7d8ece5.jpeg"
                  alt="Wellbeing activity pack for families"
                  className="max-w-full max-h-[340px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="google-ar-mental-health" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
